import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { UserPicker } from '../../components/UserPicker.js';
import { DocumentPicker } from './DocumentPicker.js';

interface ReviewRow {
  readonly reviewId: string;
  readonly actorName: string | null;
  readonly actorCode: string | null;
  readonly status: string;
  readonly decision: string | null;
  readonly reviewComments: string | null;
}

export function M23ReviewScreen(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [documentId, setDocumentId] = useState('');
  const [reviewerId, setReviewerId] = useState('');
  const [rows, setRows] = useState<ReviewRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  function load(id: string): void {
    setError(null);
    apiRequest<{ data: ReviewRow[] }>(`/v1/document-reviews/by-document/${encodeURIComponent(id)}`, { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  }

  function onLoad(e: FormEvent): void {
    e.preventDefault();
    const id = documentId.trim();
    if (!id) return;
    load(id);
  }

  function onSubmitReview(e: FormEvent): void {
    e.preventDefault();
    const id = documentId.trim();
    if (!id) return;
    setMessage(null);
    setError(null);
    apiRequest(`/v1/document-reviews/documents/${encodeURIComponent(id)}`, {
      method: 'POST',
      token,
      body: { reviewerId },
    })
      .then(() => { setMessage(t('m23.review.msgRequested')); setReviewerId(''); load(id); })
      .catch((err) => setError((err as Error).message));
  }

  return (
    <main aria-label="review-screen">
      <h1>{t('m23.review.title')}</h1>
      <form aria-label="review-load-form" onSubmit={onLoad}>
        <DocumentPicker label={t('m23.review.documentId')} value={documentId} onPick={(id) => setDocumentId(id)} />
        <button type="submit">{t('m23.review.loadReviews')}</button>
      </form>
      <form aria-label="review-submit-form" onSubmit={onSubmitReview}>
        <UserPicker label={t('m23.review.reviewerId')} testId="reviewerId" value={reviewerId} onPick={(id) => setReviewerId(id)} />
        <button type="submit">{t('m23.review.requestReview')}</button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
      {rows && (
        <table aria-label="reviews-table">
          <thead><tr><th>{t('m23.review.colReviewer')}</th><th>{t('m23.review.colStatus')}</th><th>{t('m23.review.colDecision')}</th><th>{t('m23.review.colComments')}</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.reviewId}>
                <td>{[r.actorCode, r.actorName].filter(Boolean).join(' \u2014 ')}</td>
                <td>{r.status}</td>
                <td>{r.decision ?? '—'}</td>
                <td>{r.reviewComments ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
